import React, { useState, useEffect } from 'react';
import './MyBets.css';

const ACCOUNT_API = 'https://563t3yrmw5d42zyd7gw6eeeun40htlid.lambda-url.us-east-1.on.aws';

const MyBets = ({ user, betSlipItems = [], setBetSlipItems }) => {
  const [bets, setBets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showLogBet, setShowLogBet] = useState(false);
  const [settlingBet, setSettlingBet] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('idToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchUserBets();
    fetchUserStats();
  }, []);

  const fetchUserBets = async () => {
    try {
      const response = await fetch(`${ACCOUNT_API}/api/account/bets`, {
        headers: getAuthHeader()
      });
      const data = await response.json();
      setBets(data.bets || []);
    } catch (error) {
      console.error('Failed to fetch bets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await fetch(`${ACCOUNT_API}/api/account/stats`, {
        headers: getAuthHeader()
      });
      setStats(await response.json());
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const saveBetSlipItem = async (item) => {
    try {
      await fetch(`${ACCOUNT_API}/api/account/bets`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          matchup: item.matchup,
          selection: item.selection,
          odds: item.odds,
          game_time: item.game_time || item.gameTime,
          source: 'ai_pick'
        })
      });
      fetchUserBets();
      fetchUserStats();
      // Remove from bet slip after saving
      if (setBetSlipItems) {
        setBetSlipItems(prev => prev.filter(b => b.id !== item.id));
      }
    } catch (error) {
      console.error('Failed to save bet:', error);
    }
  };

  const deleteBet = async (betId) => {
    try {
      await fetch(`${ACCOUNT_API}/api/account/bets/${betId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      fetchUserBets();
      fetchUserStats();
    } catch (error) {
      console.error('Failed to delete bet:', error);
    }
  };

  const settleBet = async (result) => {
    if (!settlingBet) return;
    try {
      await fetch(`${ACCOUNT_API}/api/account/bets/${settlingBet.bet_id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ result })
      });
      setSettlingBet(null);
      fetchUserBets();
      fetchUserStats();
    } catch (error) {
      console.error('Failed to settle bet:', error);
    }
  };

  const formatMoney = (amount) => {
    if (!amount) return '$0.00';
    const sign = amount >= 0 ? '+' : '';
    return `${sign}$${Math.abs(amount).toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'won': return '#22C55E';
      case 'lost': return '#FB7185';
      case 'pending': return '#F59E0B';
      case 'push': return '#94A3B8';
      default: return '#9AA4B2';
    }
  };

  const filteredBets = filter === 'all' 
    ? bets 
    : bets.filter(b => b.result === filter);

  if (loading) {
    return <div className="my-bets"><div className="loading">Loading your bets...</div></div>;
  }

  return (
    <div className="my-bets">
      <div className="bets-header">
        <h2>My Bets</h2>
        <button className="log-bet-btn" onClick={() => setShowLogBet(true)}>+ Log Bet</button>
      </div>

      {/* Bet Slip Preview */}
      {betSlipItems && betSlipItems.length > 0 && (
        <div className="bet-slip-preview">
          <h3>🎯 Current Bet Slip ({betSlipItems.length})</h3>
          <div className="slip-items">
            {betSlipItems.map(item => (
              <div key={item.id} className="slip-item">
                <div className="slip-selection">{item.selection}</div>
                <div className="slip-matchup">{item.matchup}</div>
                <div className="slip-odds">{item.odds > 0 ? '+' : ''}{item.odds}</div>
                <button className="save-slip-btn" onClick={() => saveBetSlipItem(item)}>Save</button>
                <button className="remove-slip-btn" onClick={() => setBetSlipItems(prev => prev.filter(b => b.id !== item.id))}>×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Overview */}
      {stats && (
        <div className="stats-overview">
          <div className="stat-card"><div className="stat-label">Total Bets</div><div className="stat-value">{stats.total_bets}</div></div>
          <div className="stat-card"><div className="stat-label">Wins</div><div className="stat-value">{stats.wins}</div></div>
          <div className="stat-card"><div className="stat-label">Losses</div><div className="stat-value">{stats.losses}</div></div>
          <div className="stat-card"><div className="stat-label">Win Rate</div><div className="stat-value">{stats.win_rate}%</div></div>
          <div className="stat-card"><div className="stat-label">ROI</div><div className={`stat-value ${stats.roi >= 0 ? 'positive' : 'negative'}`}>{stats.roi}%</div></div>
        </div>
      )}

      {/* Filters */}
      <div className="bet-filters">
        {['all', 'pending', 'won', 'lost', 'push'].map(status => (
          <button key={status} className={`filter-btn ${filter === status ? 'active' : ''}`} onClick={() => setFilter(status)}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Bet List */}
      <div className="bets-list">
        {filteredBets.length === 0 ? (
          <div className="empty-state">
            <h3>No bets found</h3>
            <p>Start tracking your bets to see your performance</p>
            <button className="log-first-bet-btn" onClick={() => setShowLogBet(true)}>Log Your First Bet</button>
          </div>
        ) : (
          filteredBets.map(bet => (
            <div key={bet.bet_id} className="bet-card">
              <div className="bet-header">
                <div className="bet-selection">{bet.selection}</div>
                <div className="bet-status" style={{ color: getStatusColor(bet.result) }}>{(bet.result || 'pending').toUpperCase()}</div>
              </div>
              <div className="bet-details">
                <div className="matchup">{bet.matchup}</div>
                <div className="bet-info">
                  {bet.stake > 0 && <span>Stake: ${bet.stake}</span>}
                  <span>Odds: {bet.odds > 0 ? '+' : ''}{bet.odds}</span>
                  <span>Source: {bet.source}</span>
                </div>
              </div>
              <div className="bet-footer">
                <div className="bet-date">{new Date(bet.created_at).toLocaleDateString()}</div>
                <div className="bet-actions">
                  {bet.result === 'pending' && (
                    <button className="settle-btn" onClick={() => setSettlingBet(bet)}>Settle</button>
                  )}
                  <button className="delete-btn" onClick={() => deleteBet(bet.bet_id)}>🗑️</button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Settle Modal */}
      {settlingBet && (
        <div className="modal-overlay" onClick={() => setSettlingBet(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Settle Bet</h3>
            <p>{settlingBet.selection}</p>
            <p>{settlingBet.matchup}</p>
            <div className="settle-buttons">
              <button className="won-btn" onClick={() => settleBet('won')}>✅ Won</button>
              <button className="lost-btn" onClick={() => settleBet('lost')}>❌ Lost</button>
              <button className="push-btn" onClick={() => settleBet('push')}>➖ Push</button>
            </div>
            <button className="cancel-btn" onClick={() => setSettlingBet(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Log Bet Modal */}
      {showLogBet && (
        <LogBetModal 
          onClose={() => setShowLogBet(false)}
          onSuccess={() => { setShowLogBet(false); fetchUserBets(); fetchUserStats(); }}
        />
      )}
    </div>
  );
};

const LogBetModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({ matchup: '', selection: '', odds: '', stake: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.matchup || !formData.selection || !formData.odds) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('idToken');
      await fetch(`${ACCOUNT_API}/api/account/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...formData, odds: parseInt(formData.odds), source: 'manual' })
      });
      onSuccess();
    } catch (error) {
      console.error('Error logging bet:', error);
      alert('Error logging bet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Log New Bet</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="log-bet-form">
          <div className="form-group">
            <label>Matchup *</label>
            <input type="text" value={formData.matchup} onChange={e => setFormData({...formData, matchup: e.target.value})} placeholder="Lakers vs Celtics" required />
          </div>
          <div className="form-group">
            <label>Selection *</label>
            <input type="text" value={formData.selection} onChange={e => setFormData({...formData, selection: e.target.value})} placeholder="Lakers -3.5" required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Odds *</label>
              <input type="number" value={formData.odds} onChange={e => setFormData({...formData, odds: e.target.value})} placeholder="-110" required />
            </div>
            <div className="form-group">
              <label>Stake ($)</label>
              <input type="number" step="0.01" value={formData.stake} onChange={e => setFormData({...formData, stake: e.target.value})} placeholder="100" />
            </div>
          </div>
          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-btn">Cancel</button>
            <button type="submit" disabled={loading} className="submit-btn">{loading ? 'Saving...' : 'Log Bet'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MyBets;
